import { Eye, PenLine, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { calculatePageSlices } from '../lib/pdfPagination';
import { createQuotePdf } from '../lib/quotePdf';
import { quoteDiscountAmount, quoteSubtotal, quoteTotal, quoteWithholdingAmount, roundMoney, type CompanyProfile, type CustomerProfile, type Plan, type Quote } from '../types';
import { QuoteDocument } from './QuoteDocument';
import { QuotePdfPages } from './QuotePdfPages';
import {thaiDateAfter} from '../../shared/thaiDate';

interface QuoteEditorProps {
  initial?: Quote | null;
  mode: 'new' | 'edit' | 'copy' | 'revision';
  customers: CustomerProfile[];
  company: CompanyProfile;
  plan: Plan;
  existingQuotes: Array<Pick<Quote, 'id' | 'number'>>;
  nextNumber: string;
  onClose: () => void;
  onSave: (quote: Quote) => Promise<void>;
}

const dateValue = (offset = 0) => thaiDateAfter(offset);

const emptyQuote = (number: string): Quote => ({
  id: crypto.randomUUID(),
  number,
  customerType: 'company',
  customerName: '',
  customerTaxId: '',
  customerBranchType: 'headOffice',
  customerBranchCode: '',
  customerContactName: '',
  customerEmail: '',
  customerPhone: '',
  customerAddress: '',
  issueDate: dateValue(),
  expiryDate: dateValue(14),
  status: 'draft',
  items: [{ id: crypto.randomUUID(), description: '', quantity: 1, unit: 'งาน', unitPrice: 0 }],
  discountType: 'amount',
  discount: 0,
  taxRate: 7,
  withholdingTaxRate: 0,
  notes: 'ใบเสนอราคานี้มีอายุ 14 วันนับจากวันที่ออกเอกสาร',
  acceptance: null,
  showProviderSignature: false,
  showSignatureSection: true,
  revision: 0,
  revisedFromId: null,
  replacedById: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  version: 1,
  deletedAt: null,
  syncState: 'pending',
});

const money = (value: number) => value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function QuoteEditor({ initial, mode, customers, company, plan, existingQuotes, nextNumber, onClose, onSave }: QuoteEditorProps) {
  const [quote, setQuote] = useState<Quote>(() => initial
    ? {
        ...structuredClone(initial),
        customerType: initial.customerType ?? 'company',
        customerTaxId: initial.customerTaxId ?? '',
        customerBranchType: initial.customerBranchType ?? 'headOffice',
        customerBranchCode: initial.customerBranchCode ?? '',
        customerContactName: initial.customerContactName ?? '',
        customerPhone: initial.customerPhone ?? '',
        discountType: initial.discountType ?? 'amount',
        withholdingTaxRate: initial.withholdingTaxRate ?? 0,
        showProviderSignature: initial.showProviderSignature ?? false,
        showSignatureSection: initial.showSignatureSection !== false,
        acceptance: initial.acceptance ?? null,
        revision: initial.revision ?? 0,
        revisedFromId: initial.revisedFromId ?? null,
        replacedById: initial.replacedById ?? null,
        items: initial.items.map((item) => ({ ...item, unit: item.unit?.trim() || 'งาน' })),
      }
    : emptyQuote(nextNumber));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(mode === 'copy' || mode === 'revision');
  const [error, setError] = useState('');
  const [mobileView, setMobileView] = useState<'form' | 'preview'>('form');
  const [isMobileLayout, setIsMobileLayout] = useState(() => window.matchMedia('(max-width: 980px)').matches);
  const [mobilePdf, setMobilePdf] = useState<Blob | null>(null);
  const [mobilePdfPageCount, setMobilePdfPageCount] = useState(0);
  const [mobilePdfLoading, setMobilePdfLoading] = useState(false);
  const [mobilePdfError, setMobilePdfError] = useState('');
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const [previewPagination, setPreviewPagination] = useState<{ pages: number; breaks: number[]; oversized: boolean }>({ pages: 1, breaks: [], oversized: false });
  const subtotal = useMemo(() => quoteSubtotal(quote), [quote]);
  const discountAmount = useMemo(() => quoteDiscountAmount(quote), [quote]);
  const withholdingAmount = useMemo(() => quoteWithholdingAmount(quote), [quote]);
  const total = useMemo(() => quoteTotal(quote), [quote]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 980px)');
    const update = () => setIsMobileLayout(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    const documentElement = document.getElementById('quote-editor-live-document');
    if (!documentElement) return;
    const measure = () => {
      const documentRect = documentElement.getBoundingClientRect();
      const coordinateScale = documentRect.width / Math.max(1, (documentElement as HTMLElement).offsetWidth);
      const relativeTop = (element: Element) => Math.max(0, Math.round((element.getBoundingClientRect().top - documentRect.top) / coordinateScale));
      const relativeBottom = (element: Element) => Math.max(0, Math.round((element.getBoundingClientRect().bottom - documentRect.top) / coordinateScale));
      const table = documentElement.querySelector('.document-table');
      const tableHead = table?.querySelector('thead');
      const tableRows = [...(table?.querySelectorAll('tbody tr') ?? [])];
      const finalBlock = documentElement.querySelector('.quote-final-block');
      if (!finalBlock) return;
      const tableBodyStart = tableRows[0] ? relativeTop(tableRows[0]) : Number.POSITIVE_INFINITY;
      const tableEnd = table ? relativeBottom(table) : 0;
      const pageWidth = Math.max(1, (documentElement as HTMLElement).offsetWidth);
      const pagination = calculatePageSlices({
        contentEnd: relativeBottom(finalBlock),
        pageHeight: Math.floor(pageWidth * 297 / 210),
        continuationTopSpace: Math.round(pageWidth * 12 / 210),
        pageNumberSpace: Math.round(pageWidth * 14 / 210),
        tableBodyStart,
        tableEnd,
        headerHeight: tableHead ? Math.max(1, relativeBottom(tableHead) - relativeTop(tableHead)) : 0,
        rowBreaks: tableRows.map(relativeBottom),
        safeBreaks: [tableBodyStart, tableEnd],
        finalBlock: { start: relativeTop(finalBlock), end: relativeBottom(finalBlock) },
      });
      setPreviewPagination({
        pages: Math.max(1, pagination.slices.length),
        breaks: pagination.slices.slice(0, -1).map((slice) => slice.end),
        oversized: pagination.oversizedFinalBlock,
      });
    };
    let active = true;
    measure();
    void document.fonts.ready.then(() => {
      if (active) measure();
    });
    const observer = new ResizeObserver(measure);
    observer.observe(documentElement);
    documentElement.querySelectorAll('.quote-final-block,.document-table,.document-table tbody tr').forEach((element) => observer.observe(element));
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [quote, company, plan, mobileView]);

  useEffect(() => {
    if (!isMobileLayout || mobileView !== 'preview') return;
    let cancelled = false;
    setMobilePdfLoading(true);
    setMobilePdfError('');
    const timer = window.setTimeout(() => {
      const documentElement = document.getElementById('quote-editor-mobile-pdf-source');
      if (!documentElement) {
        if (!cancelled) {
          setMobilePdfLoading(false);
          setMobilePdfError('เตรียมตัวอย่าง PDF ไม่สำเร็จ กรุณาลองใหม่');
        }
        return;
      }
      void createQuotePdf(documentElement, quote).then(({ blob, pageCount }) => {
        if (cancelled) return;
        setMobilePdf(blob);
        setMobilePdfPageCount(pageCount);
        setMobilePdfLoading(false);
      }).catch((reason) => {
        if (cancelled) return;
        setMobilePdfLoading(false);
        setMobilePdfError(reason instanceof Error ? reason.message : 'สร้างตัวอย่าง PDF ไม่สำเร็จ กรุณาลองใหม่');
      });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [company, isMobileLayout, mobileView, plan, quote]);

  const patch = <K extends keyof Quote>(key: K, value: Quote[K]) => {
    setDirty(true);
    setQuote((current) => ({ ...current, [key]: value }));
  };
  const patchItem = (id: string, field: 'description' | 'quantity' | 'unit' | 'unitPrice', value: string | number) => {
    patch('items', quote.items.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useLayoutEffect(() => {
    if (mobileView !== 'preview') return;
    const resetPreviewScroll = () => previewScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    resetPreviewScroll();
    const frame = window.requestAnimationFrame(resetPreviewScroll);
    const timer = window.setTimeout(resetPreviewScroll, 120);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [mobileView]);

  const requestClose = () => {
    if (dirty && !window.confirm('มีข้อมูลที่ยังไม่ได้บันทึก ต้องการปิดฟอร์มหรือไม่?')) return;
    onClose();
  };

  const chooseCustomer = (index: number) => {
    const customer = customers[index];
    if (!customer) return;
    setDirty(true);
    setQuote((current) => ({
      ...current,
      customerType: customer.type,
      customerName: customer.name,
      customerTaxId: customer.taxId,
      customerBranchType: customer.branchType,
      customerBranchCode: customer.branchCode,
      customerContactName: customer.contactName,
      customerEmail: customer.email,
      customerPhone: customer.phone,
      customerAddress: customer.address,
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (existingQuotes.some((item) => item.id !== quote.id && item.number.trim().toLocaleLowerCase() === quote.number.trim().toLocaleLowerCase())) { setError('เลขที่เอกสารนี้ถูกใช้งานแล้ว'); return; }
    if (quote.expiryDate < quote.issueDate) { setError('วันที่ใช้ได้ถึงต้องไม่น้อยกว่าวันที่ออกเอกสาร'); return; }
    if (quote.customerTaxId && !/^\d{13}$/.test(quote.customerTaxId)) { setError('เลขประจำตัวผู้เสียภาษีต้องมี 13 หลัก'); return; }
    if (quote.customerType === 'company' && quote.customerBranchType === 'branch' && !/^\d{5}$/.test(quote.customerBranchCode)) { setError('รหัสสาขาต้องมี 5 หลัก'); return; }
    if (quote.items.some((item) => !Number.isFinite(item.quantity) || item.quantity <= 0 || !Number.isFinite(item.unitPrice) || item.unitPrice < 0)) { setError('จำนวนและราคาของทุกรายการต้องเป็นตัวเลขที่ถูกต้อง'); return; }
    if (total <= 0) { setError('ยอดรวมใบเสนอราคาต้องมากกว่า 0 บาท'); return; }
    setSaving(true);
    try {
      await onSave({
        ...quote,
        number: quote.number.trim(),
        customerName: quote.customerName.trim(),
        customerTaxId: quote.customerTaxId.trim(),
        customerBranchCode: quote.customerBranchCode.trim(),
        customerContactName: quote.customerContactName.trim(),
        customerEmail: quote.customerEmail.trim(),
        customerPhone: quote.customerPhone.trim(),
        customerAddress: quote.customerAddress.trim(),
        items: quote.items.map((item) => ({ ...item, description: item.description.trim(), unit: item.unit.trim() || 'งาน' })),
        updatedAt: new Date().toISOString(),
        version: mode === 'edit' && initial ? initial.version + 1 : quote.version,
        syncState: 'pending',
      });
      setDirty(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'บันทึกเอกสารไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={requestClose}>
      <section className="modal editor-modal live-editor-modal" role="dialog" aria-modal="true" aria-labelledby="editor-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <div>
            <span className="eyebrow">{mode === 'edit' ? 'แก้ไขเอกสาร' : mode === 'revision' ? 'ฉบับแก้ไขใหม่' : mode === 'copy' ? 'สำเนาเอกสารใหม่' : 'เอกสารใหม่'}</span>
            <h2 id="editor-title">{(mode === 'edit' || mode === 'revision') && initial ? initial.number : 'สร้างใบเสนอราคา'}</h2>
          </div>
          <button className="icon-btn" type="button" onClick={requestClose} aria-label="ปิด"><X size={20} /></button>
        </header>

        <div className="editor-mobile-tabs" role="tablist" aria-label="สลับระหว่างแบบฟอร์มและตัวอย่าง">
          <button type="button" role="tab" aria-selected={mobileView === 'form'} className={mobileView === 'form' ? 'active' : ''} onClick={() => setMobileView('form')}><PenLine size={16} /> กรอกข้อมูล</button>
          <button type="button" role="tab" aria-selected={mobileView === 'preview'} className={mobileView === 'preview' ? 'active' : ''} onClick={() => setMobileView('preview')}><Eye size={16} /> ดูตัวอย่าง</button>
        </div>
        <div className={`editor-workspace mobile-${mobileView}`}>
        <form onSubmit={submit} className="editor-form editor-form-pane">
          <div className="form-section">
            <div className="section-title"><span>01</span><h3>ข้อมูลเอกสารและลูกค้า</h3></div>
            {customers.length > 0 && <label className="saved-customer"><span>เลือกลูกค้าเดิม</span><select defaultValue="" onChange={(e) => { if (e.target.value !== '') chooseCustomer(Number(e.target.value)); e.target.value = ''; }}><option value="">เลือกจากเอกสารที่ผ่านมา…</option>{customers.map((customer, index) => <option value={index} key={`${customer.taxId}-${customer.name}`}>{customer.name}{customer.taxId ? ` · ${customer.taxId}` : ''}</option>)}</select></label>}
            <div className="field-grid three">
              <label><span>เลขที่เอกสาร</span><input value={quote.number} onChange={(e) => patch('number', e.target.value)} required /></label>
              <label><span>วันที่ออก</span><input type="date" value={quote.issueDate} onChange={(e) => patch('issueDate', e.target.value)} required /></label>
              <label><span>ใช้ได้ถึง</span><input type="date" min={quote.issueDate} value={quote.expiryDate} onChange={(e) => patch('expiryDate', e.target.value)} required /></label>
            </div>
            <div className="field-grid three customer-main-fields">
              <label><span>ประเภทลูกค้า</span><select value={quote.customerType} onChange={(e) => patch('customerType', e.target.value as Quote['customerType'])}><option value="company">นิติบุคคล / บริษัท</option><option value="individual">บุคคลธรรมดา</option></select></label>
              <label><span>{quote.customerType === 'company' ? 'ชื่อบริษัท / ลูกค้า' : 'ชื่อ–นามสกุล'}</span><input value={quote.customerName} onChange={(e) => patch('customerName', e.target.value)} placeholder={quote.customerType === 'company' ? 'บริษัท ตัวอย่าง จำกัด' : 'ชื่อ นามสกุล'} required autoFocus /></label>
              <label><span>เลขประจำตัวผู้เสียภาษี</span><input inputMode="numeric" pattern="[0-9]{13}" title="กรอกเลขประจำตัวผู้เสียภาษี 13 หลัก" value={quote.customerTaxId} onChange={(e) => patch('customerTaxId', e.target.value.replace(/\D/g, '').slice(0, 13))} placeholder="เลข 13 หลัก" /></label>
            </div>
            {quote.customerType === 'company' && <div className="field-grid two">
              <label><span>สำนักงาน</span><select value={quote.customerBranchType} onChange={(e) => patch('customerBranchType', e.target.value as Quote['customerBranchType'])}><option value="headOffice">สำนักงานใหญ่</option><option value="branch">สาขา</option></select></label>
              <label><span>รหัสสาขา</span><input inputMode="numeric" pattern="[0-9]{5}" title="กรอกรหัสสาขา 5 หลัก" value={quote.customerBranchCode} onChange={(e) => patch('customerBranchCode', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder={quote.customerBranchType === 'headOffice' ? '00000' : 'เช่น 00001'} required={quote.customerBranchType === 'branch'} disabled={quote.customerBranchType === 'headOffice'} /></label>
            </div>}
            <div className="field-grid three">
              <label><span>ชื่อผู้ติดต่อ</span><input value={quote.customerContactName} onChange={(e) => patch('customerContactName', e.target.value)} placeholder="คุณ…" /></label>
              <label><span>เบอร์โทรศัพท์</span><input type="tel" value={quote.customerPhone} onChange={(e) => patch('customerPhone', e.target.value)} placeholder="08x-xxx-xxxx" /></label>
              <label><span>อีเมล</span><input type="email" value={quote.customerEmail} onChange={(e) => patch('customerEmail', e.target.value)} placeholder="contact@example.com" /></label>
            </div>
            <label><span id="quote-address-label">ที่อยู่สำหรับออกเอกสาร</span><textarea rows={3} aria-labelledby="quote-address-label" aria-describedby="quote-address-hint" value={quote.customerAddress} onChange={(e) => patch('customerAddress', e.target.value)} placeholder="บ้านเลขที่ ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์" /><small id="quote-address-hint" className="field-hint">ขึ้นบรรทัดใหม่ได้ · ชื่อ เลขผู้เสียภาษี โทรศัพท์ และอีเมล ให้กรอกในช่องแยกด้านบน</small></label>
          </div>

          <div className="form-section">
            <div className="section-title line-title">
              <div><span>02</span><h3>รายการสินค้าและบริการ</h3></div>
              <button className="btn ghost small" type="button" onClick={() => patch('items', [...quote.items, { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'งาน', unitPrice: 0 }])}><Plus size={15} /> เพิ่มรายการ</button>
            </div>
            <div className="line-items">
              <div className="line-head"><span>รายละเอียด</span><span>จำนวน</span><span>หน่วย</span><span>ราคา/หน่วย</span><span>รวม</span><span /></div>
              {quote.items.map((item) => (
                <div className="line-row" key={item.id}>
                  <label className="line-field description"><span>รายละเอียด</span><input aria-label="รายละเอียดสินค้าและบริการ" value={item.description} onChange={(e) => patchItem(item.id, 'description', e.target.value)} placeholder="เช่น ออกแบบเว็บไซต์" required /></label>
                  <label className="line-field"><span>จำนวน</span><input aria-label="จำนวน" type="number" min="0.01" step="0.01" value={item.quantity} onChange={(e) => patchItem(item.id, 'quantity', Number(e.target.value))} required /></label>
                  <label className="line-field"><span>หน่วย</span><input aria-label="หน่วย" value={item.unit ?? 'งาน'} onChange={(e) => patchItem(item.id, 'unit', e.target.value)} placeholder="งาน" maxLength={20} required /></label>
                  <label className="line-field"><span>ราคา/หน่วย</span><input aria-label="ราคาต่อหน่วย" type="number" min="0" step="0.01" value={item.unitPrice || ''} onChange={(e) => patchItem(item.id, 'unitPrice', Number(e.target.value))} placeholder="0.00" required /></label>
                  <div className="line-total"><span>รวม</span><strong>฿{money(item.quantity * item.unitPrice)}</strong></div>
                  <button className="icon-btn danger" type="button" onClick={() => patch('items', quote.items.filter((row) => row.id !== item.id))} disabled={quote.items.length === 1} aria-label="ลบรายการ"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section totals-section">
            <div className="terms-fields">
              <div className="field-grid three finance-fields">
                <label><span>ส่วนลด</span><div className="discount-input"><select value={quote.discountType} onChange={(e) => { patch('discountType', e.target.value as Quote['discountType']); patch('discount', 0); }} aria-label="รูปแบบส่วนลด"><option value="amount">บาท</option><option value="percent">%</option></select><input type="number" min="0" max={quote.discountType === 'percent' ? 100 : undefined} step="0.01" value={quote.discount || ''} onChange={(e) => patch('discount', Number(e.target.value))} placeholder="0" /></div></label>
                <label><span>VAT (%)</span><select value={quote.taxRate} onChange={(e) => patch('taxRate', Number(e.target.value))}><option value={0}>ไม่คิด VAT</option><option value={7}>7%</option></select></label>
                <label><span>หัก ณ ที่จ่าย (%)</span><input type="number" min="0" max="100" step="0.01" value={quote.withholdingTaxRate || ''} onChange={(e) => patch('withholdingTaxRate', Number(e.target.value))} placeholder="เช่น 3" /></label>
              </div>
              <label><span>หมายเหตุและเงื่อนไข</span><textarea rows={3} value={quote.notes} onChange={(e) => patch('notes', e.target.value)} /></label>
              <div className="signature-options" role="group" aria-label="ช่องลงนามใน PDF">
                <label className="signature-option"><input type="checkbox" checked={quote.showProviderSignature === true} onChange={(e) => patch('showProviderSignature', e.target.checked)} /><span><strong>แสดงลายเซ็นผู้เสนอราคา</strong><small>แสดงเฉพาะช่องลายเซ็นและวันที่</small></span></label>
                <label className="signature-option"><input type="checkbox" checked={quote.showSignatureSection !== false} onChange={(e) => patch('showSignatureSection', e.target.checked)} /><span><strong>แสดงช่องให้ลูกค้ายอมรับและลงนาม</strong><small>ลูกค้าเซ็นบนกระดาษหรือใช้โปรแกรม PDF ได้</small></span></label>
              </div>
            </div>
            <div className="total-card">
              <div><span>ยอดรวม</span><strong>฿{money(subtotal)}</strong></div>
              <div><span>ส่วนลด{quote.discountType === 'percent' ? ` ${quote.discount}%` : ''}</span><strong>−฿{money(discountAmount)}</strong></div>
              <div><span>VAT {quote.taxRate}%</span><strong>฿{money(roundMoney(Math.max(0, subtotal - discountAmount) * quote.taxRate / 100))}</strong></div>
              {quote.withholdingTaxRate > 0 && <div><span>หัก ณ ที่จ่าย {quote.withholdingTaxRate}%</span><strong>−฿{money(withholdingAmount)}</strong></div>}
              <div className="grand-total"><span>ยอดสุทธิ</span><strong>฿{money(total)}</strong></div>
            </div>
          </div>

          <footer className="modal-footer">
            {error && <p className="form-error" role="alert">{error}</p>}
            <button className="btn ghost" type="button" onClick={requestClose}>ยกเลิก</button>
            <button className="btn primary" type="submit" disabled={saving}>{saving ? 'กำลังบันทึก…' : 'บันทึกใบเสนอราคา'}</button>
          </footer>
        </form>
        <aside className="editor-live-preview" aria-label="ตัวอย่างใบเสนอราคาแบบเรียลไทม์">
          <div className="editor-live-preview-head">
            <div><span className="eyebrow">{isMobileLayout ? 'ตัวอย่างจาก PDF จริง' : 'ตัวอย่าง A4'}</span><strong>{mobilePdfLoading && isMobileLayout ? 'กำลังอัปเดตตัวอย่าง…' : 'อัปเดตตามข้อมูลที่กรอก'}</strong></div>
            <span className={`preview-page-status ${(isMobileLayout && mobilePdfError) || (!isMobileLayout && previewPagination.oversized) ? 'danger' : (isMobileLayout ? mobilePdfPageCount : previewPagination.pages) > 1 ? 'warning' : 'success'}`}>
              {isMobileLayout
                ? mobilePdfError || (mobilePdfLoading ? 'กำลังสร้าง PDF…' : mobilePdfPageCount === 1 ? 'PDF จริง · 1 หน้า' : `${mobilePdfPageCount || '—'} หน้า · PDF จริง`)
                : previewPagination.oversized ? 'ส่วนสรุปยาวเกิน 1 หน้า' : previewPagination.pages === 1 ? 'พอดี 1 หน้า A4' : `${previewPagination.pages} หน้า · แบ่งอัตโนมัติ`}
            </span>
          </div>
          <div className="live-preview-scroll" ref={previewScrollRef} key={`preview-${mobileView}`}>
            <div className="live-preview-stage desktop-live-preview">
              <QuoteDocument
                id="quote-editor-live-document"
                className="live-quote-document pdf-export"
                quote={quote}
                company={company}
                plan={plan}
                showProviderSignature={quote.showProviderSignature === true}
                showCustomerSignature={quote.showSignatureSection !== false}
                pageBreaks={previewPagination.breaks}
              />
            </div>
            <div className="mobile-pdf-preview">
              {mobilePdfLoading && !mobilePdf && <div className="mobile-pdf-message" role="status"><span className="spin" /> กำลังสร้างตัวอย่างจาก PDF จริง…</div>}
              {mobilePdfError && <div className="mobile-pdf-message error" role="alert">{mobilePdfError}</div>}
              {mobilePdf && <QuotePdfPages blob={mobilePdf} />}
            </div>
          </div>
          <div className="mobile-pdf-source" aria-hidden="true">
            <QuoteDocument
              id="quote-editor-mobile-pdf-source"
              className="live-quote-document pdf-export"
              quote={quote}
              company={company}
              plan={plan}
              showProviderSignature={quote.showProviderSignature === true}
              showCustomerSignature={quote.showSignatureSection !== false}
            />
          </div>
        </aside>
        </div>
      </section>
    </div>
  );
}
